import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Burger, Flex } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { PocketBaseService } from '@/services/pocketbase.service';
import { useMobileWidth } from '../../hooks/useMobileWidth';
import { ISearchBy, ISearchByOpts } from '../../interfaces/search';
import { exchangeService } from '../../services/exchange.service';
import { utilService } from '../../services/util.service';
import { AppLogo } from '../app-logo';
import { MobileSearchForm } from '../mobile-search-form';
import { MobileSearchTeaser } from '../mobile-search-teaser';
import Modal from '../modal';
import { OverlayScreen } from '../overlay-screen';
import { SearchForm } from '../search-form';
import { SearchTeaser } from '../search-teaser';
import { LanguageMenu } from './language-menu';
import { MobileOptionsDrawer } from './mobile-options-drawer';
import { NotificationsBell } from './notifications-bell';
import { UserMenu } from './user-menu';

export interface ISearchProps {
  activeModule: string | null;
  onChangeModule: (module: string | null) => void;
  searchBy: ISearchBy;
  isExpandedClass: string;
  onChangeIsExpanded: (value: boolean) => void;
  onSetSearchBy: (searchBy: ISearchByOpts) => void;
  handleFormSubmit: () => void;
}

const pb = PocketBaseService.getInstance().getPocketBase();

export function AppHeader() {
  const { t } = useTranslation();
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const [searchBy, setSearchBy] = useState<ISearchBy>(exchangeService.getDefaultSearch());
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isDetailsPage, _setIsDetailsPage] = useState<boolean>(false);
  const [drawerOpened, { close: closeDrawer, toggle }] = useDisclosure();
  const isMobileWidth = useMobileWidth();
  const isLogged = pb.authStore.isValid;

  const searchFormContainerRef = useRef<HTMLDivElement>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isSwapsPage = location.pathname === '/swaps';

  useEffect(() => {
    const paramsObj = Object.fromEntries(searchParams.entries());
    if (Object.keys(paramsObj).length > 0) {
      const initialSearchBy = exchangeService.getSearchFromParams(paramsObj);
      setSearchBy(initialSearchBy);
    } else {
      setSearchBy(exchangeService.getDefaultSearch());
    }
    _setIsDetailsPage(
      location.pathname.includes('/exchange/') && !location.pathname.includes('/exchange/new')
    );
  }, [location.pathname, searchParams]);

  let shouldDisplaySearchTeaser: boolean;
  if (isMobileWidth) {
    shouldDisplaySearchTeaser = isSwapsPage;
  } else {
    shouldDisplaySearchTeaser =
      isSwapsPage ||
      (location.pathname.includes('/exchange/') && !location.pathname.includes('/exchange/new'));
  }

  function handleFormSubmit() {
    const workingSearchBy = { ...searchBy };
    let needsStateUpdateForDates = false;

    if (!workingSearchBy.endDate && workingSearchBy.startDate) {
      workingSearchBy.endDate = moment(workingSearchBy.startDate).add(1, 'day').toDate();
      needsStateUpdateForDates = true;
    } else if (!workingSearchBy.startDate && workingSearchBy.endDate) {
      workingSearchBy.startDate = moment(workingSearchBy.endDate).subtract(1, 'day').toDate();
      needsStateUpdateForDates = true;
    }

    if (needsStateUpdateForDates) {
      onSetSearchBy({ startDate: workingSearchBy.startDate, endDate: workingSearchBy.endDate });
    }

    onChangeIsExpanded(false);

    const finalParams = new URLSearchParams();

    if (workingSearchBy.state && workingSearchBy.state.trim()) {
      finalParams.set('state', workingSearchBy.state);
    }
    if (workingSearchBy.city && workingSearchBy.city.trim()) {
      finalParams.set('city', workingSearchBy.city);
    }

    if (workingSearchBy.startDate) {
      finalParams.set('startDate', utilService.formatDate(workingSearchBy.startDate));
    }
    if (workingSearchBy.endDate) {
      finalParams.set('endDate', utilService.formatDate(workingSearchBy.endDate));
    }
    if (workingSearchBy.wantsMyCity) {
      finalParams.set('wantsMyCity', '1');
    }
    if (workingSearchBy.sort && workingSearchBy.sort !== 'best') {
      finalParams.set('sort', workingSearchBy.sort);
    }

    // Preserve map view when searching
    if (searchParams.get('view') === 'map') {
      finalParams.set('view', 'map');
    }

    const queryString = finalParams.toString();

    if (isDetailsPage) {
      navigate(queryString ? `/swaps?${queryString}` : '/swaps');
    } else if (queryString) {
      setSearchParams(finalParams);
    } else {
      navigate('/swaps');
    }
  }

  function onSetSearchBy(
    searchByOpts: Pick<
      ISearchByOpts,
      'state' | 'city' | 'startDate' | 'endDate' | 'wantsMyCity' | 'sort'
    >
  ) {
    setSearchBy((prev) => ({ ...prev, ...searchByOpts }));
  }

  function onChangeModule(module: string | null) {
    if (activeModule === module) {
      return;
    }
    setActiveModule(module);
  }

  function onChangeIsExpanded(value: boolean) {
    setIsExpanded(value);
    if (!value) {
      setActiveModule(null);
    }
  }

  function onBackHome() {
    navigate('/');
    setSearchBy(exchangeService.getDefaultSearch());
    setActiveModule(null);
    setIsExpanded(false);
  }

  const handleFormBlur = (ev: React.MouseEvent<HTMLDivElement>) => {
    if (!isExpanded) {
      return;
    }
    const container = searchFormContainerRef.current;
    const target = ev.target as Node;
    if (container && !container.contains(target)) {
      setActiveModule(null);
      onChangeIsExpanded(false);
    }
  };

  const wasExpandedRef = useRef<boolean>(false);
  useEffect(() => {
    if (wasExpandedRef.current && !isExpanded) {
      handleFormSubmit();
    }
    wasExpandedRef.current = isExpanded;
  }, [isExpanded]);

  const isExpandedClass: string = isExpanded ? 'expanded' : '';
  const searchProps = {
    activeModule,
    onChangeModule,
    searchBy,
    isExpandedClass,
    onChangeIsExpanded,
    onSetSearchBy,
    handleFormSubmit,
  };

  return !isMobileWidth ? (
    <div
      onClick={handleFormBlur}
      onKeyDown={(e) => e.key === 'Enter' && handleFormBlur(e as any)}
      role="button"
      tabIndex={0}
      className={`app-header ${isSwapsPage ? 'swaps-header' : ''}`}
    >
      <OverlayScreen isOpen={isExpanded} setIsOpen={() => setIsExpanded(false)} />
      <header
        className={`${isExpandedClass} ${isDetailsPage ? 'secondary-layout' : 'main-layout'} `}
      >
        <nav
          className="wrapper flex align-center justify-between"
          aria-label={t('app-header-nav-aria-label')}
        >
          <div
            onClick={() => onBackHome()}
            onKeyDown={(e) => e.key === 'Enter' && onBackHome()}
            tabIndex={0}
            role="button"
            className="logo"
          >
            <AppLogo />
          </div>
          {shouldDisplaySearchTeaser ? <SearchTeaser {...searchProps} /> : <div />}
          <div className="header-actions">
            {isLogged && (
              <>
                <button
                  type="button"
                  className="header-text-link"
                  onClick={() => navigate('/my-swaps')}
                >
                  {t('app-header-navlink-my-exchanges')}
                </button>
                {/*
                <button
                  type="button"
                  className="header-text-link"
                  onClick={() => navigate('/my-swaps')}
                >
                  {t('app-header-navlink-messages')}
                </button>
                */}
                <button
                  type="button"
                  className="header-text-link"
                  onClick={() => navigate('/my-posts')}
                >
                  {t('app-header-navlink-posts')}
                </button>
              </>
            )}
            <LanguageMenu />
            {isLogged && <NotificationsBell />}
            <UserMenu />
          </div>
        </nav>
        <div
          ref={searchFormContainerRef}
          className={`secondary-layout search-form-container ${isExpandedClass} ${
            !activeModule ? 'blured' : ''
          }`}
        >
          <SearchForm {...searchProps} />
        </div>
      </header>
    </div>
  ) : (
    <div className={`mobile-header main-layout ${isSwapsPage ? 'swaps-header' : ''}`}>
      <MobileOptionsDrawer opened={drawerOpened} onClose={closeDrawer} />

      <Flex
        className="mobile-header-container"
        direction="row"
        justify="space-between"
        align="center"
        gap="xs"
      >
        <div
          onClick={() => onBackHome()}
          onKeyDown={(e) => e.key === 'Enter' && onBackHome()}
          tabIndex={0}
          role="button"
          className="logo"
        >
          <AppLogo />
        </div>
        <Burger
          color="var(--mantine-color-nifoni-9)"
          opened={drawerOpened}
          lineSize={3}
          onClick={toggle}
          aria-label={t('app-header-burger-aria-label')}
        />
      </Flex>

      {isSwapsPage && shouldDisplaySearchTeaser && (
        <div className="mobile-search-teaser-wrap">
          <MobileSearchTeaser {...searchProps} />
        </div>
      )}

      {isExpanded && (
        <Modal isOpen={isExpanded} onClose={() => setIsExpanded(false)}>
          <MobileSearchForm {...searchProps} />
        </Modal>
      )}
    </div>
  );
}
